/**
 * Accessories PDP — template-specific orchestrator.
 *
 * Shared primitives (PdpState, PdpBaseComponent, PdpUtils, and the generic
 * <pdp-title>, <pdp-price>, <pdp-gallery>, <pdp-product-options>,
 * <pdp-sticky-cart>, etc.) are defined in pdp-core. This file only
 * contributes the `<pdp-accessories-page>` root element — it boots state
 * from the rendered JSON, optionally loads framesByGroup for the color
 * switcher, and handles the direct Add-To-Cart flow via cart-store.
 */
import Cart from 'cart-store';
import { PdpBaseComponent, PdpUtils } from 'pdp-core';

class PdpAccessoriesPage extends PdpBaseComponent {
  init() {
    this.loadInitialData();
    this.recomputeTotal();
    this.maybeLoadFramesByGroup();
  }

  setupListeners() {
    this.listen(this, 'pdp:change-variant', (e) => this.handleChangeVariant(e.detail));
    this.listen(this, 'pdp:change-frame-product', (e) => this.handleChangeFrameProduct(e.detail));
    this.listen(this, 'pdp:add-to-cart', () => this.handleAddToCart());
    this.onStateChange('selectedVariant', () => this.recomputeTotal());
  }

  recomputeTotal() {
    const variant = this.state.get('selectedVariant');
    if (!variant) return;
    this.state.setState({
      totalPrice: variant.price,
      totalCompareAtPrice: variant.compare_at_price || null,
    });
  }

  loadInitialData() {
    const dataEl = this.querySelector('[data-pdp-product-data]');
    if (!dataEl) {
      console.warn('[pdp-accessories] missing [data-pdp-product-data]');
      return;
    }

    try {
      const { product, productOptions, selectedVariant } = JSON.parse(dataEl.textContent);
      this.state.setState({
        product,
        productOptions: productOptions || [],
        selectedVariant: selectedVariant || product.variants[0],
        images: PdpUtils.buildImages(product),
        frameTag: PdpUtils.getFrameTagByProduct(product),
        frameColor: PdpUtils.getFrameColorByProduct(product),
        frameGroup: PdpUtils.getFrameGroupByProduct(product),
      });
    } catch (e) {
      console.error('[pdp-accessories] failed to parse product data', e);
    }
  }

  async maybeLoadFramesByGroup() {
    if (!this.querySelector('pdp-frame-color-switcher')) return;
    try {
      const res = await fetch('https://cdn.eazyf.com/foc/json/products_frame.json', {
        signal: this.signal,
      });
      const shopProducts = await res.json();
      const groups = new Map();
      Object.values(shopProducts).forEach((p) => {
        const tag = PdpUtils.getFrameTagByProduct(p);
        if (!tag) return;
        const group = tag.split('~')[0].split('_')[1];
        if (!groups.has(group)) groups.set(group, p);
      });

      const framesByGroup = Array.from(groups).map(([group, firstProduct]) => ({
        frameGroup: group,
        defaultFrameGroupProduct: firstProduct,
        image: firstProduct.media?.[0]?.src || null,
        allFrames: Object.values(shopProducts).filter((p) => {
          const tag = PdpUtils.getFrameTagByProduct(p);
          return tag && tag.split('~')[0].split('_')[1] === group;
        }),
      }));

      this.state.set('framesByGroup', framesByGroup);
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('[pdp-accessories] framesByGroup fetch failed', e);
    }
  }

  handleChangeVariant({ optionName, value }) {
    const product = this.state.get('product');
    const selected = this.state.get('selectedVariant');
    if (!product || !selected) return;

    const option = this.state.get('productOptions').find((o) => o.name === optionName);
    if (!option) return;

    const newOptions = [...selected.options];
    newOptions[option.position - 1] = value;

    const next = PdpUtils.findVariantByOptions(product, newOptions);
    if (next) this.state.set('selectedVariant', next);
  }

  handleChangeFrameProduct({ product }) {
    if (!product) return;
    const selectedVariant = product.variants?.[0];
    this.state.setState({
      product,
      productOptions: product.productOptions || product.options_with_values || [],
      selectedVariant,
      images: PdpUtils.buildImages(product),
      frameTag: PdpUtils.getFrameTagByProduct(product),
      frameColor: PdpUtils.getFrameColorByProduct(product),
      frameGroup: PdpUtils.getFrameGroupByProduct(product),
    });
    if (product.url) history.replaceState(null, '', product.url);
  }

  async handleAddToCart() {
    const variant = this.state.get('selectedVariant');
    if (!variant?.available || this.state.get('cartAdding')) return;

    this.state.set('cartAdding', true);
    try {
      await Cart.add({ id: variant.id, quantity: 1 });
      this.emit('pdp:cart-added', { variantId: variant.id });

      if (!this.closest('.modal--cart')) {
        const modal = document.querySelector('.modal--cart, .popup--cart-notification');
        if (modal) {
          if (!modal.shown) modal.open?.();
          else if (modal.interval) { modal.interval.reset(); modal.interval.start(); }
        }
      }
    } catch (e) {
      console.error('[pdp-accessories] add to cart failed', e);
    } finally {
      this.state.set('cartAdding', false);
    }
  }
}

if (!customElements.get('pdp-accessories-page')) {
  customElements.define('pdp-accessories-page', PdpAccessoriesPage);
}
